import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Service to manage focus after route navigation for accessibility
 * Following Angular best practices: https://angular.dev/best-practices/a11y
 */
@Injectable({
  providedIn: 'root'
})
export class FocusManagementService {
  constructor(private router: Router) {
    this.initializeFocusManagement();
  }

  /**
   * Initialize focus management on route navigation
   */
  private initializeFocusManagement(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.setFocusAfterNavigation();
      });
  }

  /**
   * Set focus to main content after navigation
   * This helps screen reader users and keyboard navigation
   */
  private setFocusAfterNavigation(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      // Try to focus on the main content header
      const mainHeader = document.querySelector<HTMLElement>('#main-content-header');
      if (mainHeader) {
        mainHeader.focus();
        return;
      }

      // Fallback: Focus on the main content area
      const mainContent = document.querySelector<HTMLElement>('main');
      if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
        mainContent.removeAttribute('tabindex');
        return;
      }

      // Last resort: Focus on router outlet container
      const routerOutlet = document.querySelector<HTMLElement>('router-outlet + *');
      if (routerOutlet) {
        routerOutlet.setAttribute('tabindex', '-1');
        routerOutlet.focus();
        routerOutlet.removeAttribute('tabindex');
      }
    }, 100);
  }

  /**
   * Manually set focus to a specific element
   * @param selector CSS selector for the element to focus
   */
  public focusElement(selector: string): void {
    setTimeout(() => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        element.focus();
      }
    }, 100);
  }

  /**
   * Focus on the first invalid form field
   * @param formId ID of the form element
   */
  public focusFirstInvalidField(formId: string): void {
    setTimeout(() => {
      const form = document.getElementById(formId);
      if (form) {
        const invalidField = form.querySelector<HTMLElement>('.ng-invalid:not(form)');
        if (invalidField) {
          invalidField.focus();
        }
      }
    }, 100);
  }

  /**
   * Announce a message to screen readers using aria-live region
   * @param message Message to announce
   * @param politeness 'polite' or 'assertive'
   */
  public announceToScreenReader(message: string, politeness: 'polite' | 'assertive' = 'polite'): void {
    const liveRegion = document.getElementById('sr-announcer');
    if (liveRegion) {
      liveRegion.setAttribute('aria-live', politeness);
      liveRegion.textContent = message;
      
      // Clear after announcement
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }
}

